<template>
    <div id="workflow-permission">
        <div class="table-header">
            <a-input-search
                :value="searchText"
                @input="searchTextHandle($event)"
                :placeholder="$t('message.search')"
                class="search-input"
                @search="onSearch"
            />
        </div>
        <div class="table-scroll-container" ref="scrollContainer" @scroll="onScroll">
            <table class="users-table">
                <thead>
                    <tr>
                        <th class="col-avatar"></th>
                        <th class="col-name">{{ $t('message.username') }}</th>
                        <th class="col-permission">{{ $t('task.deleteCard') }}</th>
                        <th class="col-permission">{{ $t('task.createCard') }}</th>
                        <th class="col-permission">{{ $t('task.editCard') }}</th>
                        <th class="col-permission">{{ $t('task.dragCard') }}</th>
                        <th class="col-permission">Kart Arşivleme</th>
                        <th class="col-permission">Kart Tamamlama</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, rowIndex) in getFilteredRows()" :key="row._id" :class="{ 'stripe-row': rowIndex % 2 === 1 }">
                        <td class="col-avatar">
                            <agent-avatar :user-i-d="row._id" :size="30" :is-named="false" />
                        </td>
                        <td class="col-name">{{ (row.name || '').length > 15 ? (row.name || '').slice(0, 11) + '...' : (row.name || '') }}</td>
                        <td class="col-permission">
                            <div class="checkbox-wrapper">
                                <div v-if="loadingStates['deleteTask_' + row._id]" class="spinner"></div>
                                <div v-else :class="['custom-checkbox', isHavePermission('deleteTask', row._id) ? 'checked' : '']" @click="toggleDeleteTaskPermission('deleteTask', row._id, !isHavePermission('deleteTask', row._id))"></div>
                            </div>
                        </td>
                        <td class="col-permission">
                            <div class="checkbox-wrapper">
                                <div v-if="loadingStates['createTask_' + row._id]" class="spinner"></div>
                                <div v-else :class="['custom-checkbox', isHavePermission('createTask', row._id) ? 'checked' : '']" @click="toggleDeleteTaskPermission('createTask', row._id, !isHavePermission('createTask', row._id))"></div>
                            </div>
                        </td>
                        <td class="col-permission">
                            <div class="checkbox-wrapper">
                                <div v-if="loadingStates['editTask_' + row._id]" class="spinner"></div>
                                <div v-else :class="['custom-checkbox', isHavePermission('editTask', row._id) ? 'checked' : '']" @click="toggleDeleteTaskPermission('editTask', row._id, !isHavePermission('editTask', row._id))"></div>
                            </div>
                        </td>
                        <td class="col-permission">
                            <div class="checkbox-wrapper">
                                <div v-if="loadingStates['draggingTask_' + row._id]" class="spinner"></div>
                                <div v-else :class="['custom-checkbox', isHavePermission('draggingTask', row._id) ? 'checked' : '']" @click="toggleDeleteTaskPermission('draggingTask', row._id, !isHavePermission('draggingTask', row._id))"></div>
                            </div>
                        </td>
                        <td class="col-permission">
                            <div class="checkbox-wrapper">
                                <div v-if="loadingStates['archiveTask_' + row._id]" class="spinner"></div>
                                <div v-else :class="['custom-checkbox', isHavePermission('archiveTask', row._id) ? 'checked' : '']" @click="toggleDeleteTaskPermission('archiveTask', row._id, !isHavePermission('archiveTask', row._id))"></div>
                            </div>
                        </td>
                        <td class="col-permission">
                            <div class="checkbox-wrapper">
                                <div v-if="loadingStates['doneTask_' + row._id]" class="spinner"></div>
                                <div v-else :class="['custom-checkbox', isHavePermission('doneTask', row._id) ? 'checked' : '']" @click="toggleDeleteTaskPermission('doneTask', row._id, !isHavePermission('doneTask', row._id))"></div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div v-if="loadingMore" class="load-more-indicator">
                <div class="spinner"></div>
                <span>Yükleniyor...</span>
            </div>
            <div v-else-if="hasMore" class="load-more-trigger" ref="loadMoreTrigger"></div>
        </div>
    </div>
</template>

<script>
import AgentAvatar from "../common/AgentAvatar.vue";
import * as _ from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";
import { addTaskPermission, removeTaskPermission } from "../../api/tasks";

export default {
    name: "PermissionWorkflow",
    props: {
        details: {
            type: Object,
        },
    },
    components: {
        AgentAvatar,
    },
    data() {
        return {
            searchText: "",
            paginationCursor: {
                total: 0,
                page: 1,
                perPage: 10,
            },
            loadingMore: false,
            supportsInfiniteScroll: false,
            cancelLoadMore: false,
            users: {
                total: 0,
                count: 0,
                data: [],
            },
            currentSort: {
                columnKey: this.$route.query.columnKey || "name",
                order: this.$route.query.order || "ascend",
            },
            form: {
                permissions: {
                    deleteTask: [],
                    editTask: [],
                    createTask: [],
                    draggingTask: [],
                    archiveTask: [],
                    doneTask: [],
                },
            },
            selectedUser: {},
            loadingStates: {},
        };
    },
    computed: {
        ...mapState({
            roles: (state) => state.role.roles,
            subjects: (state) => state.subject.subjects.data,
            loggedUser: (state) => state.auth.user || state.auth.adminUser,
            activeUserCount: (state) => state.user.activeUserCount,
            workflows: (state) => state.tasks.workflows,
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
        hasMore() {
            const getWorkflow = this.workflows.find((workflow) => workflow._id === this.details.workflowId);
            const visibleUsers = getWorkflow && getWorkflow.visibility && getWorkflow.visibility.users;
            if (!this.supportsInfiniteScroll || visibleUsers) return false;
            const loadedCount = this.users.data.length;
            return loadedCount < this.paginationCursor.total;
        },
    },
    methods: {
        ...mapActions({
            getUsers: "user/getUsers",
            getRoles: "role/getRoles",
            setWorkflows: "tasks/setWorkflows",
        }),
        getFilteredRows() {
            const getWorkflow = this.workflows.find((workflow) => workflow._id === this.details.workflowId);
            const users = this.users.data;

            if (!getWorkflow) {
                return [];
            }

            const visibleUsers = getWorkflow.visibility && getWorkflow.visibility.users;
            const query = this.searchText?.toLowerCase();

            if (visibleUsers && visibleUsers.length > 0) {
                let filteredUsers = users.filter((user) => visibleUsers.includes(user._id));

                if (query) {
                    filteredUsers = filteredUsers.filter(user =>
                        user.name.toLowerCase().includes(query) ||
                        user.email?.toLowerCase().includes(query)
                    );
                }

                return filteredUsers;
            } else {
                return users;
            }
        },
        searchTextHandle(e) {
            this.searchText = e.target.value;
        },
        onSearch() {
            this.paginationCursor.page = 1;
            this.get(false, true);
        },
        onScroll() {
            if (!this.hasMore || this.loadingMore) return;
            const container = this.$refs.scrollContainer;
            const trigger = this.$refs.loadMoreTrigger;
            if (!container || !trigger) return;
            const containerRect = container.getBoundingClientRect();
            const triggerRect = trigger.getBoundingClientRect();
            if (triggerRect.bottom <= containerRect.bottom + 100) {
                this.loadMore();
            }
        },
        loadMore() {
            if (!this.hasMore || this.loadingMore) return;
            this.paginationCursor.page = this.paginationCursor.page + 1;
            this.get(false, false, true);
        },
        isHavePermission(permissionType, userId) {
            return (this.form.permissions[permissionType] || []).includes(userId);
        },
        toggleDeleteTaskPermission(permissionType, userId, checked) {
            this.$set(this.loadingStates, `${permissionType}_${userId}`, true);

            const promiseFn = checked ? addTaskPermission : removeTaskPermission;
            promiseFn({
                workflowId: this.details.workflowId,
                userId,
                permissionType,
            })
                .then((workflow) => {
                    this.setWorkflows(
                        this.workflows.map((x) => {
                            if (x._id === this.details.workflowId) {
                                x.permissions = workflow.permissions;
                            }
                            return x;
                        })
                    );
                })
                .catch(() => {
                    this.$message.error("Kullanıcının yetkileri güncellenirken bir hata oluştu.");
                })
                .finally(() => {
                    this.$set(this.loadingStates, `${permissionType}_${userId}`, false);
                });
        },
        debouncedGet(getRoles = false, showLoading = true, interval = 0) {
            if (this.getTimerId) clearTimeout(this.getTimerId);
            this.getTimerId = setTimeout(() => {
                this.get(getRoles, showLoading);
            }, interval);
        },
        get(getRoles = false, showLoading = true, loadMore = false) {
            if (loadMore) {
                this.loadingMore = true;
                this.cancelLoadMore = false;
            } else {
                this.cancelLoadMore = true;
                if (showLoading) this.isBusy = true;
            }
            const query = this.searchText === "" && this.searchText == null ? undefined : this.searchText;
            const sorting = {
                sortBy: this.currentSort.columnKey,
                sortOrder: this.currentSort.order,
            };

            const getWorkflow = this.workflows.find((workflow) => workflow._id === this.details.workflowId);
            const visibleUsers = getWorkflow && getWorkflow.visibility && getWorkflow.visibility.users;

            if (query && visibleUsers && visibleUsers.length > 0) {
                this.supportsInfiniteScroll = false;
                this.getUsers({
                    perPage: 1000,
                    page: 1,
                    query,
                    sortBy: sorting.sortBy,
                    sortOrder: sorting.sortOrder,
                    updateStore: false,
                })
                .then((x) => {
                    if (!_.isEqual(this.users, x.data)) {
                        this.users = x.data;

                        const filteredCount = x.data.data.filter(user =>
                            visibleUsers.includes(user._id) &&
                            (user.name.toLowerCase().includes(query.toLowerCase()) ||
                             user.email?.toLowerCase().includes(query.toLowerCase()))
                        ).length;

                        this.paginationCursor.total = filteredCount;

                        if (filteredCount <= this.paginationCursor.perPage && this.paginationCursor.page > 1) {
                            this.paginationCursor.page = 1;
                        }
                    }

                    if (getRoles) {
                        this.getRoles({ perPage: 100, page: 1 });
                    }
                })
                .finally(() => {
                    if (loadMore) this.loadingMore = false;
                    if (showLoading) this.isBusy = false;
                });
            } else if (visibleUsers && visibleUsers.length > 0) {
                this.supportsInfiniteScroll = false;
                this.getUsers({
                    perPage: 100,
                    page: 1,
                    query: undefined,
                    sortBy: sorting.sortBy,
                    sortOrder: sorting.sortOrder,
                    updateStore: false,
                })
                .then((x) => {
                    if (!_.isEqual(this.users, x.data)) {
                        this.users = x.data;

                        const filteredCount = x.data.data.filter(user =>
                            visibleUsers.includes(user._id)).length;
                        this.paginationCursor.total = filteredCount;

                        if (filteredCount <= this.paginationCursor.perPage && this.paginationCursor.page > 1) {
                            this.paginationCursor.page = 1;
                        }
                    }

                    if (getRoles) {
                        this.getRoles({ perPage: 100, page: 1 });
                    }
                })
                .finally(() => {
                    if (loadMore) this.loadingMore = false;
                    if (showLoading) this.isBusy = false;
                });
            } else {
                this.supportsInfiniteScroll = true;
                this.getUsers({
                    perPage: this.paginationCursor.perPage,
                    page: this.paginationCursor.page,
                    query,
                    sortBy: sorting.sortBy,
                    sortOrder: sorting.sortOrder,
                    updateStore: false,
                })
                .then((x) => {
                    if (loadMore) {
                        if (!this.cancelLoadMore) {
                            this.users.data = this.users.data.concat(x.data.data);
                            this.users.total = x.data.total;
                        }
                    } else if (!_.isEqual(this.users, x.data)) {
                        this.users = x.data;
                        this.paginationCursor.total = x.data.total;
                    }

                    if (getRoles) {
                        this.getRoles({ perPage: 100, page: 1 });
                    }
                })
                .finally(() => {
                    if (loadMore) this.loadingMore = false;
                    if (showLoading) this.isBusy = false;
                });
            }
        },
    },
    mounted() {
        const workflow = this.workflows.find((workflow) => workflow._id === this.details.workflowId);
        this.form.permissions = workflow.permissions || this.form.permissions;
        this.get(true);
    },
    updated() {
        if (this.$validator.locale !== this.$i18n.locale) this.$validator.localize(this.$i18n.locale);
    },
    watch: {
        searchText() {
            this.paginationCursor.page = 1;
            this.debouncedGet(false, true, 400);
        },
        currentSort(newValue, oldValue) {
            if (newValue) {
                this.get(false, true);
            }
        },
        workflows: {
            deep: true,
            handler(newVal, oldVal) {
                const workflow = newVal.find((workflow) => workflow._id === this.details.workflowId);
                this.form.permissions = workflow.permissions || this.form.permissions;
            },
        },
    },
};
</script>
<style lang="scss" scoped>
#workflow-permission {
    height: 650px;
    display: flex;
    flex-direction: column;

    .table-header {
        flex-shrink: 0;
        padding: 0 0 12px 0;

        .search-input {
            max-width: 300px;
        }
    }

    .table-scroll-container {
        flex: 1;
        overflow-y: auto;
        overflow-x: auto;
        min-height: 0;
    }

    .users-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 14px;

        thead {
            position: sticky;
            top: 0;
            z-index: 1;
            background: #fafafa;
            box-shadow: 0 1px 0 0 #e8e8e8;

            th {
                padding: 12px 8px;
                text-align: left;
                font-weight: 600;
                white-space: nowrap;

                &.col-avatar {
                    width: 50px;
                }
                &.col-name {
                    min-width: 120px;
                }
                &.col-permission {
                    min-width: 100px;
                    text-align: center;
                }
            }
        }

        tbody {
            tr {
                transition: background-color 0.2s;

                &:hover {
                    background-color: #f5f5f5;
                }

                &.stripe-row {
                    background-color: #fafafa;
                }
            }

            td {
                padding: 8px;
                vertical-align: middle;
                border-bottom: 1px solid #f0f0f0;

                &.col-avatar {
                    width: 50px;
                }
                &.col-name {
                    min-width: 120px;
                }
                &.col-permission {
                    min-width: 100px;
                    text-align: center;
                }
            }
        }
    }

    .load-more-indicator {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 16px;
        color: #666;
    }

    .load-more-trigger {
        height: 1px;
        visibility: hidden;
    }

    .permissions-container {
        padding: 20px;
    }

    .modal2 {
        width: 400px !important;
    }

    .username {
        font-size: 20px;
        font-weight: bold;
        text-align: center;
        margin-bottom: 20px;
    }

    .checkbox-group {
        flex-direction: column;
        display: grid;
        gap: 10px;
    }

    .checkbox-group label {
        display: flex;
        align-items: center;
        font-size: 16px;
        padding: 5px 0;
    }

    .checkbox-group input[type="checkbox"] {
        margin-right: 10px;
        accent-color: #1e88e5;
    }
}
</style>

<style lang="scss">
.checkbox-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    padding: 5px 0;
    min-height: 40px;

    .custom-checkbox {
        position: relative;
        width: 24px;
        height: 24px;
        min-width: 24px;
        min-height: 24px;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: none;
        transition: all 0.3s;
        background-color: transparent;
        box-sizing: border-box;

        &:hover {
            &:after {
                content: "visibility";
                font-family: "Material Icons";
                position: absolute;
                display: flex;
                justify-content: center;
                align-items: center;
                font-size: 18px;
                color: #666;
                left: 0;
                top: 0;
                width: 100%;
                height: 100%;
            }
        }

        &.checked {
            &:before {
                content: "";
                position: absolute;
                display: block;
                width: 20px;
                height: 20px;
                background-color: transparent;
            }

            &:after {
                content: "";
                position: absolute;
                display: block;
                width: 6px;
                height: 12px;
                border: solid #4caf50;
                border-width: 0 2px 2px 0;
                transform: rotate(45deg);
                left: 9px;
                top: 5px;
            }
        }

        &:not(.checked):not(:hover) {
            &:after {
                content: "";
                position: absolute;
                display: block;
                width: 18px;
                height: 18px;
                border-radius: 50%;
                background-color: #666;
                opacity: 0.5;
                left: 3px;
                top: 3px;
            }
        }
    }

    .spinner {
        width: 24px;
        height: 24px;
        min-width: 24px;
        min-height: 24px;
        border: 2px solid rgba(30, 136, 229, 0.2);
        border-radius: 50%;
        border-top: 2px solid #1e88e5;
        animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }

        100% {
            transform: rotate(360deg);
        }
    }
}
</style>
